/**
 * Civil dates and wall-clock minutes, without a date library (PLAN.md §5).
 *
 * Every time in this app is ACT wall-clock, so no timezone conversion happens
 * anywhere in it. `Date` is used here only as a civil-calendar calculator —
 * constructed from explicit components via `Date.UTC(...)` and read back
 * through the `getUTC*` accessors — which makes it a pure day-counter with no
 * local-timezone or DST behaviour to leak.
 *
 * The trap this avoids is `new Date('2026-08-07')`, which parses as UTC
 * midnight and so lands on the previous day for anyone west of Greenwich.
 * Nothing here ever parses a date string through `Date`.
 *
 * The invariant that must not break: no function in this file reads the real
 * clock, with the single documented exception of `hasShiftStarted`, which
 * `src/engine/README.md` carves out and which takes `now` as an injectable
 * parameter precisely so that its impurity stops at its own signature.
 * `boundaries.test.ts` does not catch a violation of that — it bans
 * `document.`/`window.`, not `Date`/`Intl` — so it is on the reader.
 */

import type { IsoDate, Minutes } from './types'
import { MINUTES_PER_DAY } from './types'

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const MS_PER_DAY = 86_400_000

/** Days in each month, January first. February is special-cased for leap years. */
const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

export interface CivilDate {
  year: number
  month: number // 1–12
  day: number // 1–31
}

/**
 * Parse and validate an `IsoDate`.
 *
 * Validation is by round-trip, so `'2026-02-30'` throws rather than silently
 * becoming 2 March. A roster engine that accepts a nonexistent date and
 * quietly derives a different day's shift is worse than one that refuses:
 * the wrong answer arrives looking exactly like the right one.
 */
export function parseIsoDate(date: IsoDate): CivilDate {
  const match = ISO_DATE.exec(date)
  if (!match) throw new RangeError(`Not an ISO date: ${JSON.stringify(date)}`)

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  const utc = new Date(Date.UTC(year, month - 1, day))
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    throw new RangeError(`No such date: ${date}`)
  }

  return { year, month, day }
}

export function toIsoDate({ year, month, day }: CivilDate): IsoDate {
  const y = String(year).padStart(4, '0')
  const m = String(month).padStart(2, '0')
  const d = String(day).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Whole days since the Unix epoch. The engine's one notion of "which day" —
 * everything that orders, subtracts or cycles dates goes through this.
 */
export function dayNumber(date: IsoDate): number {
  const { year, month, day } = parseIsoDate(date)
  return Date.UTC(year, month - 1, day) / MS_PER_DAY
}

/** Shift a date by whole days. Negative counts go backwards. */
export function addDays(date: IsoDate, days: number): IsoDate {
  const utc = new Date((dayNumber(date) + days) * MS_PER_DAY)
  return toIsoDate({
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  })
}

/** Signed day count from `earlier` to `later`. Negative when they are swapped. */
export function diffDays(later: IsoDate, earlier: IsoDate): number {
  return dayNumber(later) - dayNumber(earlier)
}

/**
 * Shift a date by whole months, clamping to the end of the target month:
 * `addMonths('2026-01-31', 1)` is `'2026-02-28'`, not 3 March.
 *
 * Used only for the roster horizon (`monthsBack`/`monthsForward`), where
 * rolling over into the next month would quietly generate a month more of
 * roster than the config asked for.
 */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const { year, month, day } = parseIsoDate(date)

  const monthIndex = year * 12 + (month - 1) + months
  const targetYear = Math.floor(monthIndex / 12)
  const targetMonth = floorMod(monthIndex, 12) + 1

  return toIsoDate({
    year: targetYear,
    month: targetMonth,
    day: Math.min(day, daysInMonth(targetYear, targetMonth)),
  })
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: IsoDate): number {
  const { year, month, day } = parseIsoDate(date)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

/** The first of the month a date falls in. The month grid's top-left anchor. */
export function startOfMonth(date: IsoDate): IsoDate {
  const { year, month } = parseIsoDate(date)
  return toIsoDate({ year, month, day: 1 })
}

/**
 * How many days a month has. Computed from the Gregorian leap rule rather than
 * from `Date`, because `Date.UTC` maps years 0–99 onto 1900–1999 and would
 * give the wrong February for them without any error.
 */
export function daysInMonth(year: number, month: number): number {
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`No such month: ${month}`)
  }
  if (month === 2 && (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0))) {
    return 29
  }
  return MONTH_LENGTHS[month - 1]
}

/**
 * Euclidean modulo: always a result in `[0, m)` for positive `m`.
 *
 * **The reason this file exists.** JavaScript's `%` takes the sign of its left
 * operand, so `-1 % 8` is `-1`. In the cycle-day calculation
 * (`src/engine/roster.ts`), the `− blockIndex` term is negative for blocks 3B,
 * 4A and 4B *on the anchor date itself* — three cells of the 64-cell golden
 * fixture, not a distant edge case. A plain `%` there would index
 * `cycleDayShifts` with a negative number and silently hand back the wrong
 * shift for those cells. Never write `%` where a cycle position is wanted.
 */
export function floorMod(n: number, m: number): number {
  if (m <= 0) throw new RangeError(`floorMod needs a positive modulus, got ${m}`)
  return ((n % m) + m) % m
}

/**
 * A single point on the wall clock, as minutes from the Unix epoch.
 *
 * This is the one integer space all rest and overlap arithmetic happens in.
 * Two shifts on different dates are ordered and subtracted here without ever
 * becoming instants, which is why DST cannot enter: a 10h break across the
 * spring-forward night is 9 real hours, and we still call it 10, because that
 * is how the roster and everyone working it talks about a break.
 */
export function absoluteMinutes(date: IsoDate, minute: Minutes): number {
  return dayNumber(date) * MINUTES_PER_DAY + minute
}

/**
 * The formatter for `hasShiftStarted`, built once.
 *
 * The locale is pinned rather than left to the environment's default: the
 * parts are read numerically, and a default locale with a non-Latin numbering
 * system or a non-Gregorian calendar would hand back digits `Number()` cannot
 * read, or a Buddhist-era year. `hour12: false` resolves to the `h23` cycle,
 * but `hourCycle` is ignored whenever `hour12` is present, so midnight is
 * normalised below rather than trusted to come back as `'00'`.
 */
const SYDNEY_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Australia/Sydney',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/**
 * Has the shift starting at `startMin` on `date` already begun?
 *
 * **The one impurity in `src/engine/`**, carved out in `src/engine/README.md`.
 * The undo guard genuinely needs to know "right now", and that cannot be
 * answered from `IsoDate`/`Minutes` alone — it needs a real instant converted
 * to ACT wall-clock, which is this single `Intl.DateTimeFormat` call.
 *
 * `now` is a parameter with a default precisely so the function is testable
 * without faking the clock. Keep it that way; a caller passing its own `now`
 * is using this correctly, not working around it.
 */
export function hasShiftStarted(date: IsoDate, startMin: Minutes, now: Date = new Date()): boolean {
  const parts = SYDNEY_PARTS.formatToParts(now)

  const part = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type)
    if (!found) throw new Error(`Intl gave no ${type} part for Australia/Sydney`)
    return Number(found.value)
  }

  const nowDate = toIsoDate({ year: part('year'), month: part('month'), day: part('day') })
  // `% 24` folds an `h24`-style midnight ('24:00' on the day it ends) back to
  // hour 0. On this runtime `hour12: false` resolves to `h23` and it is a
  // no-op; it costs nothing and removes a dependency on ICU's behaviour here.
  const nowMinutes = (part('hour') % 24) * 60 + part('minute')

  return absoluteMinutes(nowDate, nowMinutes) >= absoluteMinutes(date, startMin)
}
